// File: frontend/src/components/marketing/LandingSections.js
//
// The content sections of the landing page, grouped to avoid over-fragmentation:
// TrustStrip, HowItWorks (#how-it-works),
// SolutionsSection (#solutions), SecuritySection, PricingSection (#pricing),
// FinalCTA.
//
// Every claim maps to real, verified PesaFii capability (routers, packages,
// vouchers, wallet, withdrawals, the login-page customiser, sales/transactions,
// usage analytics, users/agents, roles, router scoping, tenant separation,
// audit logs). Named providers are the ones production actually runs: MikroTik
// routers, and MTN Mobile Money and Airtel Money for both collections and
// withdrawals. No invented features, customers, counts or superlatives, and NO
// password-recovery links. Copy speaks in operator outcomes, not in backend
// terms ("tenant", "scope", "audit visibility").
//
// Design-system discipline (see frontend/audit.md §2–4, §7.3):
//   • Colour = meaning: neutral tokens dominate; the single emerald brand accent
//     (`primary.main`) is the only accent — no decorative purples/sky-blues here.
//   • Typography: the theme scale only (overline / h1 / h3 / subtitle / body),
//     no improvised rem sizes and no 800+ weights.
//   • Spacing: MUI 4/8 units.
//   • One high-emphasis primary action (FinalCTA "Start Free", contained);
//     "Sign In" is the lower-emphasis outlined secondary.

import React from "react";
import { Box, Button, Container, Stack, Typography, Paper, useTheme, alpha } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { PRICING, COLLECTION_FEE_TOTAL_PCT } from "../../content/publicPricing";
import MarketingMobileVisual from "./visuals/MarketingMobileVisual";
import { useReveal } from "./landingMotion";

// ── shared heading ──────────────────────────────────────────────────────────
function SectionHeading({ eyebrow, title, subtitle, center = true }) {
  return (
    <Box sx={{ textAlign: center ? "center" : "left", maxWidth: 720, mx: center ? "auto" : 0, mb: { xs: 4, md: 6 } }}>
      {eyebrow && (
        <Typography data-reveal="" variant="overline" sx={{ display: "block", color: "primary.main", mb: 1 }}>
          {eyebrow}
        </Typography>
      )}
      <Typography data-reveal="" variant="h1" component="h2" sx={{ color: "text.primary" }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography data-reveal="" variant="body1" sx={{ mt: 1.5, color: "text.secondary" }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

// Every section reveals its [data-reveal] parts once, as they scroll in
// (landingMotion.useReveal; nothing above the fold is ever hidden).
function SectionWrap({ id, children, sx }) {
  const ref = useReveal();
  return (
    <Box component="section" id={id} ref={ref} sx={{ py: { xs: 6, md: 9 }, scrollMarginTop: 80, ...sx }}>
      <Container maxWidth="lg" sx={{ minWidth: 0 }}>{children}</Container>
    </Box>
  );
}

// ── Trust strip ─────────────────────────────────────────────────────────────
// Text chips rather than logos: the integrations are real, but no brand assets
// have been approved for use here.
const TRUST_ITEMS = ["Works with MikroTik", "MTN Mobile Money", "Airtel Money", "Prices in UGX"];

export function TrustStrip() {
  return (
    <Box component="section" aria-label="Supported routers and payment networks" sx={{ py: { xs: 3, md: 4 }, borderTop: "1px solid", borderBottom: "1px solid", borderColor: "divider", bgcolor: "action.hover" }}>
      <Container maxWidth="lg">
        <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 1.5, md: 3 }} alignItems="center" justifyContent="center" sx={{ textAlign: "center" }}>
          <Typography variant="subtitle2" component="p" sx={{ color: "text.secondary" }}>
            Built for hotspot operators in Uganda.
          </Typography>
          <Stack component="ul" direction="row" useFlexGap flexWrap="wrap" justifyContent="center" columnGap={1} rowGap={1} sx={{ listStyle: "none", m: 0, p: 0 }}>
            {TRUST_ITEMS.map((t) => (
              <Typography
                key={t}
                component="li"
                variant="subtitle2"
                sx={{ color: "text.primary", px: 1.5, py: 0.5, borderRadius: 99, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
              >
                {t}
              </Typography>
            ))}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

// ── How it works ────────────────────────────────────────────────────────────
const STEPS = [
  { n: 1, title: "Connect your router", desc: "Add and configure a supported MikroTik hotspot router." },
  { n: 2, title: "Create packages and payment options", desc: "Set durations, prices, vouchers and supported customer payment methods." },
  { n: 3, title: "Monitor and grow", desc: "Track users, sales, revenue, transactions and router performance." },
];

export function HowItWorks() {
  return (
    <SectionWrap id="how-it-works" sx={{ bgcolor: "action.hover" }}>
      <SectionHeading eyebrow="How it works" title="Live in three steps" subtitle="You need a MikroTik router and an internet connection. PesaFii gives you the setup script and checks the connection." />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: { xs: 2, md: 3 } }}>
        {STEPS.map((s) => (
          <Paper data-reveal="" key={s.n} sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "divider", height: "100%", position: "relative", minWidth: 0 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "50%", mb: 2, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "primary.main" }}>
              <Typography variant="h3" component="span" sx={{ color: "primary.contrastText" }}>{s.n}</Typography>
            </Box>
            <Typography variant="h3" component="h3" sx={{ color: "text.primary", mb: 1 }}>{s.title}</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>{s.desc}</Typography>
          </Paper>
        ))}
      </Box>
    </SectionWrap>
  );
}

// ── Solutions / local relevance ─────────────────────────────────────────────
const LOCAL_POINTS = [
  "Prices in Uganda shillings",
  "Voucher codes for cash sales",
  "MTN and Airtel Mobile Money",
  "Reports for each location",
  "Sell through voucher agents",
  // "Every router in one dashboard" read as a compatibility claim rather than a
  // scope one. PesaFii talks to MikroTik over the RouterOS REST API and has no
  // adapter for any other vendor, so the promise is about all of YOUR routers.
  "All your routers in one dashboard",
];

export function SolutionsSection() {
  return (
    <SectionWrap id="solutions">
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.25fr 1fr" }, gap: { xs: 4, md: 6 }, alignItems: "center" }}>
        <Box sx={{ minWidth: 0 }}>
          <SectionHeading center={false} eyebrow="Solutions" title="Built for how Wi-Fi is sold in Uganda" subtitle="PesaFii fits how internet businesses actually run day to day — from voucher sales to router-level revenue reconciliation." />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, minWidth: 0 }}>
            {LOCAL_POINTS.map((p) => (
              <Stack data-reveal="" key={p} direction="row" spacing={1.5} alignItems="center" sx={{ p: 2, borderRadius: 2.5, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main", flexShrink: 0 }} />
                <Typography variant="subtitle2" sx={{ color: "text.primary" }}>{p}</Typography>
              </Stack>
            ))}
          </Box>
        </Box>
        {/* On-the-go mobile product visual */}
        <Box sx={{ minWidth: 0, display: "flex", justifyContent: "center" }}>
          <MarketingMobileVisual />
        </Box>
      </Box>
    </SectionWrap>
  );
}

// ── Security & control ──────────────────────────────────────────────────────
const SECURITY = [
  { icon: LockOutlinedIcon, title: "Secure account access", desc: "Verified accounts with email verification and protected sign-in." },
  { icon: VerifiedUserOutlinedIcon, title: "Staff see only what they need", desc: "Give each team member and agent only the access their role needs." },
  { icon: LayersOutlinedIcon, title: "Focus on one location", desc: "See the performance and controls for each hotspot location on its own." },
  { icon: ShieldOutlinedIcon, title: "Your business data stays yours", desc: "Each business sees only its own routers, customers and money." },
  { icon: PaidOutlinedIcon, title: "Withdrawals need your PIN", desc: "Sending money out needs a withdrawal PIN, and only people you allow can do it." },
  { icon: HistoryOutlinedIcon, title: "A record of important actions", desc: "Key account and money actions are recorded, so you can see who did what." },
];

export function SecuritySection() {
  const theme = useTheme();
  return (
    <SectionWrap sx={{ bgcolor: "action.hover" }}>
      <SectionHeading eyebrow="Security & control" title="Controls for your money and your staff" subtitle="Practical safeguards built into how PesaFii handles accounts, roles and money movement." />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: { xs: 2, md: 3 } }}>
        {SECURITY.map(({ icon: Icon, title, desc }) => (
          <Stack data-reveal="" key={title} direction="row" spacing={1.5} sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider", bgcolor: "background.paper", minWidth: 0 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: 2, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: alpha(theme.palette.primary.main, 0.12), color: "primary.main" }}>
              <Icon sx={{ fontSize: 19 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" component="h3" sx={{ color: "text.primary", mb: 0.5 }}>{title}</Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>{desc}</Typography>
            </Box>
          </Stack>
        ))}
      </Box>
    </SectionWrap>
  );
}

// ── Pricing ─────────────────────────────────────────────────────────────────
// Figures come from content/publicPricing.js, which is checked against the
// charging code and the Terms. Withdrawal fees are banded, so the page says
// where the amount is shown rather than quoting a number.
const PRICE_ITEMS = [
  {
    label: "Account",
    value: "Free",
    note: `${PRICING.subscription}. Registration is free.`,
  },
  {
    label: "Mobile Money payments",
    value: `${COLLECTION_FEE_TOTAL_PCT}%`,
    note: `Of each customer payment: ${PRICING.platformFeePct}% PesaFii fee plus ${PRICING.gatewayFeePct}% payment provider fee. The rest goes to your wallet.`,
  },
  {
    label: "Voucher activation",
    value: `${PRICING.voucherActivationPct}%`,
    note: "Of the voucher's price, charged once, the first time the voucher is used. Creating and printing vouchers is free.",
  },
  {
    label: "Withdrawals",
    value: "Quoted first",
    note: "The withdrawal fee depends on the amount and is shown before you confirm.",
  },
];

export function PricingSection() {
  const theme = useTheme();
  return (
    <SectionWrap id="pricing">
      <SectionHeading eyebrow="Pricing" title="Simple, transaction-based pricing" subtitle="You set your own package prices. PesaFii is paid from the payments it processes for you." />
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: { xs: 2, md: 3 } }}>
        {PRICE_ITEMS.map((item) => (
          <Paper data-reveal="" component="li" key={item.label} sx={{ p: 3, borderRadius: 3, border: "1px solid", borderColor: "divider", minWidth: 0 }}>
            <Typography variant="subtitle2" component="h3" sx={{ color: "text.secondary" }}>{item.label}</Typography>
            <Typography component="p" sx={{ mt: 1, fontSize: "2rem", fontWeight: 700, lineHeight: 1.1, color: "text.primary" }}>{item.value}</Typography>
            <Typography variant="body2" sx={{ mt: 1.5, color: "text.secondary" }}>{item.note}</Typography>
          </Paper>
        ))}
      </Box>
      <Typography variant="body2" sx={{ mt: 3, textAlign: "center", color: "text.secondary" }}>
        All amounts are in Uganda shillings.{" "}
        <Box component={RouterLink} to={PRICING.termsHref} sx={{ color: "primary.main", fontWeight: 600, "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 } }}>
          Read the full fee terms
        </Box>
      </Typography>
    </SectionWrap>
  );
}

// ── Final CTA ───────────────────────────────────────────────────────────────
export function FinalCTA() {
  const theme = useTheme();
  const green = theme.palette.primary.main;
  return (
    <SectionWrap>
      <Paper data-reveal="fade" sx={{
        borderRadius: 4, p: { xs: 4, md: 7 }, textAlign: "center", position: "relative", overflow: "hidden",
        border: "1px solid", borderColor: alpha(green, 0.3),
        background: theme.palette.mode === "dark"
          ? `radial-gradient(600px 300px at 50% -20%, ${alpha(green, 0.16)}, transparent 70%), ${theme.palette.background.paper}`
          : `radial-gradient(600px 300px at 50% -20%, ${alpha(green, 0.12)}, transparent 70%), ${theme.palette.background.paper}`,
      }}>
        <Typography variant="h1" component="h2" sx={{ color: "text.primary", mb: 1.5 }}>
          Start selling Wi-Fi with PesaFii
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 560, mx: "auto", mb: 4 }}>
          Registration is free and there is no monthly subscription. PesaFii is paid from the sales it processes for you.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
          <Button component={RouterLink} to="/login?mode=register" variant="contained" size="large" sx={{ borderRadius: 2, px: 4, py: 1.5 }}>
            Start Free
          </Button>
          <Button component={RouterLink} to="/login?mode=login" variant="text" size="large" sx={{ borderRadius: 2, px: 4, py: 1.5, color: "text.secondary", "&:hover": { color: "primary.main", bgcolor: alpha(green, 0.06) } }}>
            Sign In
          </Button>
        </Stack>
        <Typography variant="body2" sx={{ mt: 3, color: "text.secondary" }}>
          Questions first?{" "}
          <Box component={RouterLink} to="/contact" sx={{ color: "primary.main", fontWeight: 600, "&:focus-visible": { outline: `2px solid ${green}`, outlineOffset: 2 } }}>
            Talk to the PesaFii team
          </Box>
        </Typography>
      </Paper>
    </SectionWrap>
  );
}
